import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import httpService from '../../services/httpService';
import { ApiEndpoints, Constants } from '../../constants/appConstants';
import { useExcelImportPopup } from '../Common/useExcelImportPopup';
import { useDataManagementFileUpload } from '../Common/useDataManagementFileUpload';
import { ImportTemplates } from '../../constants/appConstants';
import { createDialogOverlayStyles } from '../../utils/safeCssInjection';
import './QuestionsComponent.scss';
import { Toast } from 'primereact/toast';
import { sanitize } from '../../services/renderingService';
import SafeHtml from '../Common/SafeHtml';
import DataManagementForm from '../DataManagement/DataManagementForm';
import { dataManagementFormConfig, getFormValidationErrors, normalizeFieldValues } from '../DataManagement/dataManagementFormConfig';
import DataManagementLoader from '../DataManagement/DataManagementLoader';
import DataManagementDataTable from '../DataManagement/DataManagementDataTable';
import { Column } from 'primereact/column';
import { createDataManagementQuery, useDataManagementPagination } from '../DataManagement/useDataManagementPagination';

interface QuestionTypeOption {
  label: string;
  value: string;
  isOptionRequired: boolean;
}

interface QuestionOption {
  questionOptionId?: number;
  optionText: string;
  sequence: number | '';
}

const fetchTiers = () => {
  const url = `${ApiEndpoints.GetTiers}`;
  return httpService.get(url);
};

const fetchMeasures = () => {
  const url = `${ApiEndpoints.GetAllMeasures}`;
  return httpService.get(url);
};

const fetchStandards = () => {
  const url = `${ApiEndpoints.GetAllStandards}`;
  return httpService.get(url);
};

const fetchPerformanceAreas = () => {
  const url = `${ApiEndpoints.GetAllPerformanceAreas}`;
  return httpService.get(url);
};

const fetchQuestionTypes = () => {
  const url = `${ApiEndpoints.GetQuestionTypes}`;
  return httpService.get(url);
};

const getQuestionTypeOptions = (response: any): QuestionTypeOption[] => {
  const responseData = response?.data;
  const questionTypeData = Array.isArray(responseData)
    ? responseData
    : Array.isArray(responseData?.data)
      ? responseData.data
      : Array.isArray(responseData?.items)
        ? responseData.items
        : [];

  return questionTypeData
    .map((questionType: any) => ({
      label: questionType.QuestionType ?? questionType.questionType,
      value: questionType.QuestionTypeCode ?? questionType.questionTypeCode,
      isOptionRequired: questionType.IsOptionRequired === true || questionType.isOptionRequired === true,
    }))
    .filter((option: { label?: string; value?: string }) => option.label && option.value);
};

const getQuestionOptionValidationErrors = (question: any, questionTypes: QuestionTypeOption[]) => {
  const selectedQuestionType = questionTypes.find(questionType => questionType.value === question?.dataFieldType);
  if (!selectedQuestionType?.isOptionRequired) return [];

  const options: QuestionOption[] = Array.isArray(question?.options) ? question.options : [];
  if (options.length === 0) return ['At least one option is required'];
  if (options.some(option => !String(option.optionText ?? '').trim())) return ['Option text is required'];
  if (options.some(option => option.sequence === '' || option.sequence === null || option.sequence === undefined)) return ['Option sequence is required'];
  return [];
};

interface QuestionsComponentProps {
  providerTypes: { label: string; value: string }[];
}

const QuestionsComponent: React.FC<QuestionsComponentProps> = ({ providerTypes }) => {
  React.useEffect(() => {
    const cleanup = createDialogOverlayStyles('questions-component-styles');
    return cleanup;
  }, []);

  const [tiers, setTiers] = useState<any[]>([]);
  const [measures, setMeasures] = useState<any[]>([]);
  const [standards, setStandards] = useState<any[]>([]);
  const [performanceAreas, setPerformanceAreas] = useState<any[]>([]);
  const [questionTypes, setQuestionTypes] = useState<QuestionTypeOption[]>([]);
  const [lookupsLoading, setLookupsLoading] = useState(true);
  const [lookupError, setLookupError] = useState('');
  const toast = React.useRef<Toast>(null);
  const { uploadFile } = useDataManagementFileUpload();

  const handleExcelUpload = async (file: File) => {
    setLoading(true);
    try {
      const fileKey = await uploadFile(file, Constants.DATA_MANAGEMENT_MODULES.QUESTIONS);
      await httpService.post(ApiEndpoints.BulkUploadDataManagement, {
        fileKey,
        fileName: file.name,
        module: Constants.DATA_MANAGEMENT_MODULES.QUESTIONS,
      });
      await refresh();
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Questions uploaded successfully', life: 3000 });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Bulk upload failed";
      toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const { showImport, ImportPopup } = useExcelImportPopup({
    templateUrl: ImportTemplates.questions,
    onUpload: handleExcelUpload,
    title: 'Import Questions from Excel',
    prominentDownloadLink: true,
  });

  const [providerTypeFilter, setProviderTypeFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<any>(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<any>(null);

  const query = createDataManagementQuery({ providerType: providerTypeFilter, search: searchTerm });
  const {
    items: questions,
    totalRecords,
    page,
    setPage,
    loading,
    setLoading,
    error: pageError,
    refresh,
  } = useDataManagementPagination<any>(ApiEndpoints.GetPagedQuestions, query, {
    debounceMs: searchTerm ? 300 : 0,
    errorPrefix: 'Failed to fetch questions',
    preserveItemsOnError: true,
    recoverEmptyPageWhenNoResults: true,
  });
  const error = pageError || lookupError;

  useEffect(() => {
    setLookupsLoading(true);
    Promise.all([fetchTiers(), fetchMeasures(), fetchStandards(), fetchPerformanceAreas(), fetchQuestionTypes()])
      .then(([tiersRes, measuresRes, standardsRes, performanceAreasRes, questionTypesRes]: any[]) => {
        setTiers(tiersRes.data);
        setMeasures(measuresRes.data);
        setStandards(standardsRes.data);
        setPerformanceAreas(performanceAreasRes.data);
        setQuestionTypes(getQuestionTypeOptions(questionTypesRes));
      })
      .catch((err: any) => {
        setLookupError(`Failed to fetch question form lookups: ${err.message}`);
      })
      .finally(() => setLookupsLoading(false));
  }, []);

  const providerTypeOptions = providerTypes && providerTypes.length > 0
    ? providerTypes
    : Array.from(new Set(questions.map(q => q.providerType).filter(Boolean))).map(pt => ({ label: pt, value: pt }));

  const editQuestion = (question: any) => { setSelectedQuestion({ ...question }); setIsAddMode(false); setEditDialogVisible(true); };
  const deleteQuestion = (question: any) => { setQuestionToDelete(question); setDeleteDialogVisible(true); };

  const tierOptions = tiers
    .filter((t: any) => t.tierCode)
    .map((t: any) => ({ label: t.tierCode, value: t.tierCode, tierId: t.tierId, providerType: t.providerType }));

  const measureOptions = measures
    .filter((m: any) => m.measureNumber)
    .map((m: any) => ({ label: m.measureNumber, value: m.measureNumber, measureId: m.measureId, providerType: m.providerType }));

  return (
    <div style={{ marginTop: 24, position: 'relative', minHeight: 220 }} aria-busy={loading}>
      <DataManagementLoader visible={loading || lookupsLoading} label="Loading questions" />
      <Toast ref={toast} position="top-right" style={{ zIndex: 9999 }} />
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {ImportPopup}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label="Add New Question" icon="pi pi-plus" onClick={() => {
            setSelectedQuestion({
              questionId: 0,
              reviewYear: '',
              reviewConfigId: null,
              performanceAreaTitle: '',
              performanceAreaId: null,
              standardTitle: '',
              standardId: null,
              tierCode: '',
              measureNumber: '',
              questionText: '',
              number: null,
              dataFieldType: '',
              options: [],
              dataSource: '',
              sequence: null,
              conditionalDisplay: '',
              providerType: '',
              questionHtmlText: ''
            });
            setIsAddMode(true);
            setEditDialogVisible(true);
          }} />
          <Button label="Import" icon="pi pi-upload" className="p-button-secondary" onClick={showImport} style={{ marginLeft: 8 }} />
        </div>
        <div className="question-search-anim">
          <input
            type="text"
            placeholder="Search by Question, Measure, or Tier..."
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
          />
          <span className="pi pi-search" />
        </div>
        <div className="provider-type-dropdown">
          <select
            value={providerTypeFilter}
            onChange={e => { setProviderTypeFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Provider Types</option>
            {providerTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className="pi pi-angle-down" />
        </div>
      </div>
      <DataManagementDataTable value={questions} dataKey="questionId" page={page} totalRecords={totalRecords}
        onPage={setPage} loading={loading || lookupsLoading} emptyMessage="No questions found."
        tableStyle={{ minWidth: '1200px', background: '#fff' }}>
        <Column header="#" body={(_question: any, options) => (page - 1) * 10 + options.rowIndex + 1} headerStyle={{ width: 64, minWidth: 40 }} />
        <Column field="tierCode" header="Tier Code" body={(question: any) => question.tierCode || '-'} />
        <Column field="standardTitle" header="Standard Title" body={(question: any) => question.standardTitle || '-'} />
        <Column field="measureNumber" header="Measure Number" body={(question: any) => question.measureNumber || '-'} />
        <Column field="questionText" header="Question Text" body={(question: any) => (
          <span style={{ display: 'block', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <SafeHtml html={question.questionHtmlText || '-'} />
          </span>
        )} />
        <Column field="dataFieldType" header="Data Field Type" body={(question: any) => question.dataFieldType || '-'} />
        <Column field="sequence" header="Sequence" body={(question: any) => question.sequence ?? '-'} />
        <Column field="reviewYear" header="Review Year" body={(question: any) => question.reviewYear || '-'} />
        <Column field="providerType" header="Provider Type" body={(question: any) => question.providerType || '-'} />
        <Column header="Actions" body={(question: any) => (
          <>
            <Button icon="pi pi-pencil" className="p-button-text" style={{ marginRight: 8 }} onClick={() => editQuestion(question)} />
            <Button icon="pi pi-trash" className="p-button-text p-button-danger" onClick={() => deleteQuestion(question)} />
          </>
        )} />
      </DataManagementDataTable>
      <Dialog
        header={isAddMode ? "Add Question" : `Edit Question${selectedQuestion && selectedQuestion.questionText ? ` - ${selectedQuestion.questionText.substring(0, 30)}...` : ''}`}
        visible={editDialogVisible}
        style={{ width: '700px', maxWidth: '95vw' }}
        onHide={() => { setEditDialogVisible(false); setIsAddMode(false); }}
        footer={
          <div>
            <Button label="Cancel" onClick={() => { setEditDialogVisible(false); setIsAddMode(false); }} className="p-button-text" />
            <Button label="Save" onClick={async () => {
              const validationErrors = [
                ...getFormValidationErrors(dataManagementFormConfig.questions, selectedQuestion),
                ...getQuestionOptionValidationErrors(selectedQuestion, questionTypes),
              ];
              if (validationErrors.length > 0) {
                toast.current?.show({ severity: 'warn', summary: 'Fix the following', detail: validationErrors.join(', '), life: 4000 });
                return;
              }
              const normalizedPayload = normalizeFieldValues(dataManagementFormConfig.questions, selectedQuestion);
              const payload = {
                ...normalizedPayload,
                questionHtmlText: sanitize(normalizedPayload?.questionHtmlText || ''),
              };
              if (isAddMode) {
                try {
                  setLoading(true);
                  await httpService.post(ApiEndpoints.AddQuestion, payload);
                  await refresh();
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Question added successfully', life: 3000 });
                } catch (err: any) {
                  const msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setTimeout(() => {
                    setEditDialogVisible(false);
                    setIsAddMode(false);
                  }, 100);
                } finally {
                  setLoading(false);
                }
              } else if (selectedQuestion) {
                try {
                  setLoading(true);
                  await httpService.put(`${ApiEndpoints.UpdateQuestion}`, { ...payload, isActive: selectedQuestion.isActive });
                  await refresh();
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Question updated successfully', life: 3000 });
                } catch (err: any) {
                  const msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setTimeout(() => {
                    setEditDialogVisible(false);
                    setIsAddMode(false);
                  }, 100);
                } finally {
                  setLoading(false);
                }
              }
            }} loading={loading} disabled={loading} autoFocus />
          </div>
        }
      >
        <QuestionEditForm
          editQuestion={selectedQuestion}
          setEditQuestion={setSelectedQuestion}
          providerTypes={providerTypes}
          tierOptions={tierOptions}
          measureOptions={measureOptions}
          standards={standards}
          performanceAreas={performanceAreas}
          questionTypes={questionTypes}
        />
      </Dialog>
      <Dialog
        header="Confirm Delete"
        visible={deleteDialogVisible}
        style={{ width: '350px' }}
        onHide={() => setDeleteDialogVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Yes" autoFocus={false} onClick={async () => {
              if (!questionToDelete) return;
              try {
                setLoading(true);
                await httpService.delete(`${ApiEndpoints.DeleteQuestion}/${questionToDelete.questionId}`);
                await refresh();
                setDeleteDialogVisible(false);
                setQuestionToDelete(null);
              } catch (err: any) {
                const msg = err?.response?.data?.message || err?.message || 'Failed to delete question';
                toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
              } finally {
                setLoading(false);
              }
            }} className="p-button-danger" />
            <Button label="No" autoFocus={true} onClick={() => setDeleteDialogVisible(false)} className="p-button-secondary" />
          </div>
        }
      >
        <div style={{ fontSize: 16, margin: '16px 0' }}>
          Are you sure you want to delete this question{questionToDelete && questionToDelete.questionText ? `: ${questionToDelete.questionText.substring(0, 50)}...` : ''}?
        </div>
      </Dialog>
    </div>
  );
}

interface QuestionEditFormProps {
  editQuestion: any;
  setEditQuestion: (question: any) => void;
  providerTypes: { label: string; value: string }[];
  tierOptions: { label: string; value: string; tierId: number; providerType: string }[];
  measureOptions: { label: string; value: string; measureId: number; providerType: string }[];
  standards: any[];
  performanceAreas: any[];
  questionTypes: QuestionTypeOption[];
}

const QuestionEditForm: React.FC<QuestionEditFormProps> = ({ editQuestion, setEditQuestion, providerTypes, tierOptions, measureOptions, standards, performanceAreas, questionTypes }) => {
  const [reviewYears, setReviewYears] = useState<any[]>([]);

  useEffect(() => {
    const providerType = editQuestion?.providerType;
    if (!providerType) {
      setReviewYears([]);
      return;
    }

    let isCurrent = true;
    httpService.get(`${ApiEndpoints.GetAllActiveReviewYearDetails}/${encodeURIComponent(providerType)}`)
      .then((response: any) => {
        if (isCurrent) setReviewYears(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        if (isCurrent) setReviewYears([]);
      });

    return () => {
      isCurrent = false;
    };
  }, [editQuestion?.providerType]);

  const filteredTierOptions = editQuestion?.providerType
    ? tierOptions.filter(tier => tier.providerType === editQuestion.providerType)
    : tierOptions;
  const filteredMeasureOptions = editQuestion?.providerType
    ? measureOptions.filter(measure => measure.providerType === editQuestion.providerType)
    : measureOptions;
  const filteredPerformanceAreas = performanceAreas.filter(area =>
    (!editQuestion?.providerType || area.providerType === editQuestion.providerType) ||
    String(area.performanceAreaId) === String(editQuestion?.performanceAreaId) ||
    area.performanceAreaTitle === editQuestion?.performanceAreaTitle
  );
  const filteredStandards = standards.filter(standard =>
    (!editQuestion?.providerType || standard.providerType === editQuestion.providerType) &&
    (!editQuestion?.performanceAreaId
      ? standard.performanceAreaTitle === editQuestion?.performanceAreaTitle
      : String(standard.performanceAreaId) === String(editQuestion.performanceAreaId)) ||
    String(standard.standardId) === String(editQuestion?.standardId) ||
    standard.title === editQuestion?.standardTitle
  );
  const selectedQuestionType = questionTypes.find(questionType => questionType.value === editQuestion?.dataFieldType);
  const showOptions = !!selectedQuestionType?.isOptionRequired;
  const questionOptions: QuestionOption[] = Array.isArray(editQuestion?.options) ? editQuestion.options : [];

  const handleQuestionChange = (nextQuestion: any) => {
    if (nextQuestion.dataFieldType !== editQuestion?.dataFieldType) {
      nextQuestion.options = [];
    }
    setEditQuestion(nextQuestion);
  };

  const updateOption = (index: number, field: 'optionText' | 'sequence', value: string) => {
    const nextOptions = questionOptions.map((option, optionIndex) => optionIndex === index
      ? { ...option, [field]: field === 'sequence' && value !== '' ? Number(value) : value }
      : option);
    setEditQuestion({ ...editQuestion, options: nextOptions });
  };

  const addOption = () => {
    const nextSequence = questionOptions.reduce((highest, option) => Math.max(highest, Number(option.sequence) || 0), 0) + 1;
    setEditQuestion({ ...editQuestion, options: [...questionOptions, { optionText: '', sequence: nextSequence }] });
  };

  const removeOption = (index: number) => {
    setEditQuestion({ ...editQuestion, options: questionOptions.filter((_, optionIndex) => optionIndex !== index) });
  };

  return (
    <>
      <DataManagementForm
        fields={dataManagementFormConfig.questions}
        values={editQuestion}
        onChange={handleQuestionChange}
        options={{
          providerType: providerTypes,
          reviewYear: reviewYears.map(reviewYear => ({
            label: String(reviewYear.reviewYear ?? reviewYear.ReviewYear ?? ''),
            value: reviewYear.reviewYear ?? reviewYear.ReviewYear,
            id: reviewYear.reviewConfigId ?? reviewYear.reviewConfigurationId ?? reviewYear.ReviewConfigurationId,
          })),
          performanceAreaTitle: filteredPerformanceAreas.map(area => ({ label: area.performanceAreaTitle, value: area.performanceAreaTitle, id: area.performanceAreaId })),
          standardTitle: filteredStandards.map(standard => ({ label: standard.title, value: standard.title, id: standard.standardId })),
          tierCode: filteredTierOptions.map(tier => ({ label: tier.label, value: tier.value, id: tier.tierId })),
          measureNumber: filteredMeasureOptions.map(measure => ({ label: measure.label, value: measure.value, id: measure.measureId })),
          dataFieldType: questionTypes,
        }}
      />
      {showOptions && (
        <div className="question-options-editor">
          <div className="question-options-header">
            <span className="dm-form-label">Options <span className="dm-required" aria-label="required">*</span></span>
            <Button type="button" label="Add Option" icon="pi pi-plus" size="small" onClick={addOption} />
          </div>
          {questionOptions.map((option, index) => (
            <div className="question-option-row" key={option.questionOptionId ?? index}>
              <input
                type="text"
                aria-label={`Option ${index + 1} text`}
                placeholder="Option text"
                value={option.optionText ?? ''}
                onChange={event => updateOption(index, 'optionText', event.target.value)}
              />
              <input
                type="number"
                aria-label={`Option ${index + 1} sequence`}
                min={1}
                placeholder="Sequence"
                value={option.sequence ?? ''}
                onChange={event => updateOption(index, 'sequence', event.target.value)}
              />
              <Button type="button" icon="pi pi-trash" className="p-button-text p-button-danger" aria-label={`Remove option ${index + 1}`} onClick={() => removeOption(index)} />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default QuestionsComponent;
